import { LineChart, type LineSeries } from '@mui/x-charts';
import { type FC } from 'react';

import type { PanelInfo, PanelData } from '../../types/monitoringTypes';
import SpinnerLoader from '../Loaders/SpinnerLoader';

interface ChartValues {
  xAxis: { data: Date[]; scaleType: 'time' }[];
  series: {
    curve: string;
    label: string;
    data: number[];
    showMark: boolean;
  }[];
}

interface TimeSeriesProps {
  panelInfo: PanelInfo;
  height: number;
  isLoading: boolean;
  panelData?: PanelData | undefined;
}

const TimeSeries: FC<TimeSeriesProps> = ({ panelInfo, height, isLoading, panelData }) => {
  const getPanelValues = (): ChartValues => {
    if (!panelData || Object.keys(panelData).length === 0) {
      return { xAxis: [], series: [] };
    }

    try {
      const refIds =
        panelInfo.targets
          ?.filter(
            (target): target is typeof target & { refId: string } =>
              !target.hide && Boolean(target.refId)
          )
          .map(target => target.refId) ?? [];
      const frames = refIds.map(refId => panelData.results[refId]?.frames[0]).filter(Boolean);

      if (frames.length === 0 || !frames[0]?.data.values) {
        return { xAxis: [], series: [] };
      }

      const firstPanel = frames[0];
      const timeValues = firstPanel.data.values[0];

      if (!timeValues || timeValues.length === 0) {
        return { xAxis: [], series: [] };
      }

      const xAxis = timeValues
        .filter((v): v is number => typeof v === 'number')
        .map(v => new Date(v));

      const series = firstPanel.data.values.slice(1).map((d, index) => {
        const field = firstPanel.schema?.fields[index + 1];
        const label = field?.labels
          ? `${field.name} ${Object.values(field.labels).join(' ')}`
          : (field?.name ?? `Series ${String(index + 1)}`);

        return {
          curve: 'linear',
          label,
          data: d.map(v => (typeof v === 'number' ? v : 0)),
          showMark: false,
        };
      });

      return {
        xAxis: [{ data: xAxis, scaleType: 'time' }],
        series,
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error while getting panel values:', message);
      return { xAxis: [], series: [] };
    }
  };

  const values = getPanelValues();

  return (
    <SpinnerLoader
      isLoading={isLoading}
      height={height}
      labelText='Loading time series data...'
    >
      <LineChart
        key={panelInfo.title}
        xAxis={values.xAxis}
        series={values.series as readonly LineSeries[]}
        colors={['#E20074', '#04e000', '#f399c7']}
        grid={{ vertical: true, horizontal: true }}
        height={height}
      />
    </SpinnerLoader>
  );
};

export default TimeSeries;
